import type { Country } from "../Country.js";
import type { CountrySettings } from "../CountrySettings.js";
import type { Currency } from "../Currency.js";
import type { EconomicSubjectVatType } from "../EconomicSubjectVatType.js";
import type { ExchangeRate } from "../ExchangeRatesSetting.js";
import type { ProductForCustomer } from "../Product.js";
import type { SubcategoryForCustomer } from "../Subcategory.js";
import type { SubscriptionParamsForPricing } from "../Subscription.js";
import type { SubscriptionDeliveryCount } from "../SubscriptionDeliveryCount.js";
import { SubscriptionKind } from "../SubscriptionKind.js";
import type { TaxedPrice } from "../TaxedPrice.js";
import { calculatePriceOfGiftSubscription } from "./calculatePriceOfGiftSubscription.js";
import { calculatePriceOfRecurringSubscription } from "./calculatePriceOfRecurringSubscription.js";

/**
 * Dispatches to the gift or recurring subscription pricing based on `subscription.kind`.
 */
export function calculatePriceOfSubscription({
	subcategories,
	destinationCountry,
	subscription,
	mothersBlendProduct,
	cafeinaProduct,
	currency,
	vatType,
	exchangeRates,
	countrySettings,
	mothersBlendConstantPriceEnabled,
	deliveryCount,
}: {
	subcategories: SubcategoryForCustomer[];
	destinationCountry: Country | "outsideEu";
	subscription: SubscriptionParamsForPricing;
	mothersBlendProduct: ProductForCustomer;
	cafeinaProduct: ProductForCustomer;
	currency: Currency;
	vatType: EconomicSubjectVatType;
	exchangeRates: ExchangeRate[];
	countrySettings: CountrySettings[];
	mothersBlendConstantPriceEnabled: boolean;
	deliveryCount: SubscriptionDeliveryCount | undefined;
}): TaxedPrice {
	if (subscription.kind === SubscriptionKind.Gift) {
		if (deliveryCount === undefined) {
			throw new Error("Gift subscriptions require a deliveryCount");
		}

		return calculatePriceOfGiftSubscription({
			subcategories,
			destinationCountry,
			subscription,
			currency,
			vatType,
			exchangeRates,
			deliveryCount,
		});
	}

	return calculatePriceOfRecurringSubscription({
		subcategories,
		destinationCountry,
		subscription,
		mothersBlendProduct,
		cafeinaProduct,
		currency,
		vatType,
		exchangeRates,
		countrySettings,
		mothersBlendConstantPriceEnabled,
	});
}
